/**
 * API Configuration
 *
 * The Vite bundle reads `import.meta.env.VITE_API_BASE_URL` at build time and
 * inlines it into the bundle (see Dockerfile.frontend / docker-compose.*).
 * The default in `Dockerfile.frontend` is `/api`, so production bundles hit
 * the same origin and the reverse proxy (Caddy / Nginx) routes `/api/*` to
 * the backend.
 *
 * The runtime fallback below ONLY exists for local LAN development where the
 * bundle is loaded from a Vite dev server and the API runs on `:3000` of the
 * same host. For any real domain we MUST default to the same-origin `/api`,
 * otherwise the browser would try `https://<domain>:3000/api/...` which
 * doesn't speak TLS → `ERR_SSL_PROTOCOL_ERROR`.
 */

const isLocalHostname = (hostname: string): boolean => {
  if (hostname === 'localhost' || hostname === '0.0.0.0') return true;
  // Bare IPv4 (e.g. 192.168.1.10) — no dotted TLD, just digits + dots.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return true;
  // IPv6 loopback / link-local addresses are bracketed by the URL parser.
  if (hostname === '[::1]' || hostname.startsWith('[fe80:')) return true;
  return false;
};

export const getApiBaseUrl = (): string => {
  // Build-time override always wins.
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // SSR / non-browser fallback.
  if (typeof window === 'undefined') {
    return '/api';
  }

  const { protocol, hostname } = window.location;

  // Local LAN development: backend is on :3000 of the same host.
  if (isLocalHostname(hostname)) {
    return `${protocol}//${hostname}:3000/api`;
  }

  // Any real hostname (FQDN, tunnel, prod domain) — same-origin, proxied by Caddy/Nginx.
  return '/api';
};

export const API_BASE_URL = getApiBaseUrl();
